import React, { useEffect, useState } from "react";
import { Card, Col, Nav, Row, Tab, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { LandingDashboardService } from "./service";
import { ErrorToast } from "../../shared/toasters/toasters";
import LoadingSpinner from "../statistics/components/LoadingSpinner";

type LandingStatistics = Record<string, number>;

interface GraphData {
  categories: string[];
  series: { name: string; data: number[] }[];
}

interface LandingCoach {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  landing: string;
  coachProfileSetupProgress?: string;
  isActive?: boolean;
  isEmailVerified?: boolean;
}

interface LandingData {
  statistics: LandingStatistics;
  graphs: GraphData;
  latestCoaches: LandingCoach[];
}

const LandingDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<LandingData | null>(null);
  const [activeTab, setActiveTab] = useState("statistics");

  useEffect(() => {
    LandingDashboardService.getLanding().then(setData).catch(ErrorToast);
  }, []);

  const formatDate = (iso: string) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleDateString();
  };

  const chartOptions: ApexOptions = {
    chart: { type: "area", toolbar: { show: true }, zoom: { enabled: true } },
    stroke: { curve: "smooth", width: 2 },
    fill: { type: "gradient", gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
    xaxis: {
      categories: data?.graphs.categories ?? [],
      labels: { rotate: -45, style: { fontSize: "11px" } },
    },
    yaxis: { min: 0, labels: { formatter: (v) => Math.floor(v).toString() } },
    legend: { position: "top" },
    tooltip: { x: { format: "yyyy-MM-dd" } },
    colors: ["#4680ff", "#2ca87f"],
    markers: { size: 3 },
    grid: { borderColor: "#e0e0e0" },
  };

  return (
    <div className="landing-dashboard-page">
      <Tab.Container
        activeKey={activeTab}
        onSelect={(k) => k && setActiveTab(k)}
      >
        <Card className="mb-4">
          <Card.Body className="py-2">
            <Nav variant="pills" className="gap-1">
              <Nav.Item>
                <Nav.Link eventKey="statistics">
                  <i className="ph-duotone ph-chart-pie-slice me-2" />
                  {t("landing.tabStatistics")}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="graphs">
                  <i className="ph-duotone ph-chart-line me-2" />
                  {t("landing.tabGraphs")}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="coaches">
                  <i className="ph-duotone ph-users me-2" />
                  {t("landing.tabCoaches")}
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Body>
        </Card>

        <Tab.Content>
          {/* Statistics Tab */}
          <Tab.Pane eventKey="statistics">
            {!data ? (
              <LoadingSpinner />
            ) : (
              <Row className="g-4">
                <Col xl={12}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">{t("landing.landingPages")}</h6>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div className="table-responsive">
                        <Table hover borderless className="mb-0 small">
                          <thead className="table-light">
                            <tr>
                              <th>{t("landing.landingField")}</th>
                              <th className="text-end">{t("landing.count")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(data.statistics).map(
                              ([key, count]) => (
                                <tr key={key}>
                                  <td>
                                    <span className="badge bg-primary rounded-pill me-2">
                                      {key}
                                    </span>
                                  </td>
                                  <td className="text-end fw-semibold">
                                    {count}
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Tab.Pane>

          {/* Graphs Tab */}
          <Tab.Pane eventKey="graphs">
            {!data ? (
              <LoadingSpinner />
            ) : (
              <Row className="g-4">
                <Col xl={12}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">
                        {t("landing.dailyActivity")} -{" "}
                        {t("statistics.last30Days")}
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <ReactApexChart
                        options={chartOptions}
                        series={data.graphs.series}
                        type="area"
                        height={380}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Tab.Pane>

          {/* Latest Coaches Tab */}
          <Tab.Pane eventKey="coaches">
            {!data ? (
              <LoadingSpinner />
            ) : (
              <Row className="g-4">
                <Col xl={12}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">{t("landing.latestCoaches")}</h6>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div className="table-responsive">
                        <Table hover borderless className="mb-0 small">
                          <thead className="table-light">
                            <tr>
                              <th>{t("statistics.name")}</th>
                              <th>{t("statistics.email")}</th>
                              <th>{t("landing.landingField")}</th>
                              <th>{t("statistics.registered")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.latestCoaches.map((c) => (
                              <tr key={c.id}>
                                <td>
                                  {c.firstName} {c.lastName}
                                </td>
                                <td className="text-muted">{c.email}</td>
                                <td>
                                  <span className="badge bg-primary rounded-pill">
                                    {c.landing}
                                  </span>
                                </td>
                                <td>{formatDate(c.createdAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default LandingDashboard;
